import { useCallback, useEffect, useRef, useState } from 'react';
import { AccountReceivable, AccountReceivablesService, Customer, AccountReceivableDataMedium } from '@/api';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X, Loader2 } from 'lucide-react';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@googlemaps/js-api-loader';
import { zodResolver } from '@hookform/resolvers/zod';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast } from 'sonner';
import { z } from 'zod';
import { useForm } from 'react-hook-form';

// ------------------------------
// Zod Schema for Validation
// ------------------------------
const updateAccountReceivableFormSchema = z
  .object({
    name: z.string().min(2, { message: 'Client name must be at least 2 characters.' }),
    loanId: z.string(),
    description: z.string().nullable().optional(),
    firstName: z.string(),
    lastName: z.string(),
    middleName: z.string().nullable().optional(),
    contactEmail: z.string().email({ message: 'Invalid email address.' }),
    contactPhone: z.string(),
    customerType: z.enum(['Individual', 'Business', 'Organization']),
    balance: z.string(),
    invoiceDate: z.date(),
    dueDate: z.date(),
    amountPaid: z.string(),
    amountDue: z.string(),
    streetAddress1: z.string(),
    streetAddress2: z.string(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
    country: z.string(),
    status: z.enum(['Unpaid', 'PartiallyPaid', 'Paid', 'Overdue', 'Cancelled', 'VoluntaryChurned', 'InvoluntaryChurned', 'VoluntaryChurnedRecovered', 'InvoluntaryChurnedRecovered']),
    quickBooksInvoiceLink: z.string().nullable().optional(),
    stripeInvoiceLink: z.string().nullable().optional(),
    customInvoiceLink: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.dueDate <= data.invoiceDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Due date must be later than the invoice date.',
        path: ['dueDate'],
      });
    }
  });

// ------------------------------
// Helper: Parse Google Address Components
// ------------------------------
function parseAddressComponents(
  addressComponents: google.maps.GeocoderAddressComponent[],
) {
  const address = {
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  };

  addressComponents.forEach((component) => {
    const type = component.types[0];
    switch (type) {
      case 'street_number':
        address.streetAddress1 = `${component.long_name} ${address.streetAddress1}`.trim();
        break;
      case 'route':
        address.streetAddress1 = `${address.streetAddress1} ${component.long_name}`.trim();
        break;
      case 'subpremise':
        address.streetAddress2 = component.long_name;
        break;
      case 'locality':
        address.city = component.long_name;
        break;
      case 'administrative_area_level_1':
        address.state = component.short_name;
        break;
      case 'country':
        address.country = component.long_name;
        break;
      case 'postal_code':
        address.postalCode = component.long_name;
        break;
      default:
        break;
    }
  });

  return address;
}

// ------------------------------
// Types & Props
// ------------------------------
export interface EditingAccountReceivable {
  id: string;
  name: string;
  loanId: string;
  portfolioId: string;
  customer: Customer;
  balance: number;
  invoiceDate: Date;
  dueDate: Date;
  amountPaid: number;
  amountDue: number;
  accountReceivableDataMedium: AccountReceivableDataMedium;
}

interface Props {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingAccountReceivable: EditingAccountReceivable;
}

// ------------------------------
// The Modal Component
// ------------------------------
export function EditAccountReceivableModal({
  isOpen,
  setOpen,
  editingAccountReceivable,
}: Props) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Example store usage (assumed to exist)
  const org = useStore($org);
  const ar = useStore($accountreceivable);
  const po = useStore($portfolio);

  // React Hook Form Setup
  const updateAccountReceivableForm = useForm<z.infer<typeof updateAccountReceivableFormSchema>>({
    resolver: zodResolver(updateAccountReceivableFormSchema),
    defaultValues: {
      name: '',
      loanId: '',
      firstName: '',
      lastName: '',
      middleName: '',
      contactEmail: '',
      contactPhone: '',
      customerType: 'Individual',
      balance: '0',
      invoiceDate: new Date(),
      dueDate: new Date(),
      amountPaid: '0',
      amountDue: '0',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      status: 'Unpaid',
      quickBooksInvoiceLink: '',
      stripeInvoiceLink: '',
      customInvoiceLink: '',
    },
  });

  // Google Places Autocomplete Setup
  const [autoService, setAutoService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const placesServiceRef = useRef<HTMLDivElement>(null);

  const initServices = useCallback(async () => {
    if (autoService && placesService) return;
    try {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_ADDRESS_API_KEY,
        libraries: ['places'],
      });
      await loader.load();
      const newAutoService = new google.maps.places.AutocompleteService();
      setAutoService(newAutoService);
      if (placesServiceRef.current) {
        const newPlacesService = new google.maps.places.PlacesService(placesServiceRef.current);
        setPlacesService(newPlacesService);
      } else {
        throw new Error('PlacesService ref is not attached to a DOM element.');
      }
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
      toast.error('Failed to load address autocomplete. Please try again later.');
    }
  }, [autoService, placesService]);

  const fetchPlacePredictions = async (value: string) => {
    if (!autoService || !placesService) {
      await initServices();
    }
    if (autoService) {
      autoService.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'us' },
          types: ['address'],
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }
  };

  const handleAddressChange = async (value: string) => {
    updateAccountReceivableForm.setValue('streetAddress1', value);
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }
    await fetchPlacePredictions(value);
  };

  const handleSelectSuggestion = (placeId: string, description: string) => {
    setSuggestions([]);
    updateAccountReceivableForm.setValue('streetAddress1', description);
    if (!placesService) return;
    placesService.getDetails(
      { placeId, fields: ['address_components'] },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.address_components
        ) {
          const parsed = parseAddressComponents(place.address_components);
          updateAccountReceivableForm.setValue('streetAddress1', parsed.streetAddress1);
          updateAccountReceivableForm.setValue('streetAddress2', parsed.streetAddress2);
          updateAccountReceivableForm.setValue('city', parsed.city);
          updateAccountReceivableForm.setValue('state', parsed.state);
          updateAccountReceivableForm.setValue('postalCode', parsed.postalCode);
          updateAccountReceivableForm.setValue('country', parsed.country);
        }
      }
    );
  };

  // Query for fresh data
  const { data: freshData } = useQuery({
    queryKey: ['accountReceivable', editingAccountReceivable?.id],
    queryFn: async () => {
      if (!editingAccountReceivable?.id) {
        throw new Error('No account receivable ID provided');
      }
      return await AccountReceivablesService.getAccountReceivable(editingAccountReceivable.id);
    },
    enabled: isOpen && !!editingAccountReceivable?.id,
  });

  useEffect(() => {
    if (isOpen && freshData?.accountReceivable) {
      const ar = freshData.accountReceivable;
      updateAccountReceivableForm.reset({
        name: ar.name || '',
        loanId: ar.loanId || '',
        firstName: ar.customer.firstName || '',
        lastName: ar.customer.lastName || '',
        middleName: ar.customer.middleName || '',
        contactEmail: ar.customer.contactEmail || '',
        contactPhone: ar.customer.contactPhone.startsWith('+')
          ? ar.customer.contactPhone.slice(1)
          : ar.customer.contactPhone || '',
        customerType: ar.customer.customerType || 'Individual',
        balance: ar.balance.toString() || '0',
        invoiceDate: new Date(ar.invoiceDate ?? ''),
        dueDate: new Date(ar.dueDate ?? ''),
        amountPaid: ar.amountPaid.toString() || '0',
        amountDue: ar.amountDue.toString() || '0',
        streetAddress1: ar.customer.streetAddress || '',
        streetAddress2: ar.customer.streetAddress2 || '',
        city: ar.customer.city || '',
        state: ar.customer.state || '',
        postalCode: ar.customer.postalCode || '',
        country: ar.customer.country || '',
        status: ar.status || 'Unpaid',
        quickBooksInvoiceLink: ar.quickBooksInvoiceLink || '',
        stripeInvoiceLink: ar.stripeInvoiceLink || '',
        customInvoiceLink: ar.customInvoiceLink || '',
      });
    }
  }, [isOpen, freshData, updateAccountReceivableForm]);

  // Submit Handler
  async function onSubmit(values: z.infer<typeof updateAccountReceivableFormSchema>) {
    try {
      setIsLoading(true);
      if (!editingAccountReceivable?.id) {
        toast.error('No account receivable ID found');
        return;
      }

      const formData = { ...values, invoiceDate: values.invoiceDate, dueDate: values.dueDate };

      const result = await AccountReceivablesService.updateAccountReceivable(
        editingAccountReceivable.id,
        {
          name: formData.name,
          loanId: formData.loanId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          middleName: formData.middleName,
          contactEmail: formData.contactEmail,
          contactPhone: `+${formData.contactPhone}`,
          customerType: formData.customerType,
          balance: parseFloat(formData.balance),
          invoiceDate: formData.invoiceDate.toISOString(),
          dueDate: formData.dueDate.toISOString(),
          amountPaid: parseFloat(formData.amountPaid),
          amountDue: parseFloat(formData.amountDue),
          streetAddress1: formData.streetAddress1,
          streetAddress2: formData.streetAddress2,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
          status: formData.status,
          quickBooksInvoiceLink: formData.quickBooksInvoiceLink,
          stripeInvoiceLink: formData.stripeInvoiceLink,
          customInvoiceLink: formData.customInvoiceLink,
        }
      );

      if (result.succeeded) {
        const fresh = await AccountReceivablesService.getAccountReceivable(editingAccountReceivable.id);
        if (fresh?.accountReceivable) {
          $accountreceivable.set(fresh.accountReceivable as AccountReceivable);
          const arr = $accountreceivables.get();
          if (!arr?.items) return;
          const idx = arr.items.findIndex(a => a.id === editingAccountReceivable.id);
          if (idx > -1) {
            arr.items[idx] = fresh.accountReceivable as AccountReceivable;
            $accountreceivables.set(arr);
          }
        }
        toast.success('Account receivable updated successfully');
        setOpen(false);
      } else {
        toast.error(result.message || 'Failed to update account receivable');
      }
    } catch (error) {
      toast.error('An error occurred while updating the account receivable');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      updateAccountReceivableForm.reset();
      setOpen(false);
    }
  }, [isOpen, updateAccountReceivableForm, setOpen]);

  // Auto-calculate Amount Due = Balance - Amount Paid
  const balanceVal = updateAccountReceivableForm.watch('balance');
  const amountPaidVal = updateAccountReceivableForm.watch('amountPaid');

  useEffect(() => {
    const b = parseFloat(balanceVal) || 0;
    const p = parseFloat(amountPaidVal) || 0;
    updateAccountReceivableForm.setValue('amountDue', (b - p).toString());
  }, [balanceVal, amountPaidVal, updateAccountReceivableForm]);

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent
        className="
          max-w-[90vw]
          max-h-[90vh]
          bg-white
          p-4 md:p-6
          rounded-lg
          shadow-xl
          flex
          flex-col
        "
      >
        {/* Hidden element for Google Places */}
        <div ref={placesServiceRef} style={{ display: 'none' }} />

        {/* Top Bar with Title and Actions */}
        <div className="flex items-center justify-between border-b pb-2">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">
              Edit Account
            </h2>
            <p className="text-sm text-gray-500">
              Update the details below.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="editARForm"
              disabled={isLoading}
              className="px-3 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update
            </Button>
          </div>
        </div>

        {/* The Form in Three Columns */}
        <Form {...updateAccountReceivableForm}>
          <form
            id="editARForm"
            onSubmit={updateAccountReceivableForm.handleSubmit(onSubmit)}
            className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {/* Column 1 */}
            <div className="space-y-4">
              <FormField
                control={updateAccountReceivableForm.control}
                name="status"
                render={() => (
                  <FormItem className="w-full">
                    <FormLabel>Status</FormLabel>
                    <Tabs
                      value={updateAccountReceivableForm.watch('status')}
                      onValueChange={(value) =>
                        updateAccountReceivableForm.setValue(
                          'status',
                          value as 'Unpaid' | 'PartiallyPaid' | 'Paid' | 'Overdue' | 'Cancelled' | 'VoluntaryChurned' | 'InvoluntaryChurned' | 'VoluntaryChurnedRecovered' | 'InvoluntaryChurnedRecovered'
                        )
                      }
                      className="w-full"
                    >
                      <TabsList className="flex flex-wrap gap-1 bg-gray-50 p-1 rounded-lg h-auto">
                        <TabsTrigger value="Unpaid" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Unpaid</TabsTrigger>
                        <TabsTrigger value="PartiallyPaid" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Partial</TabsTrigger>
                        <TabsTrigger value="Paid" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Paid</TabsTrigger>
                        <TabsTrigger value="Overdue" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Overdue</TabsTrigger>
                        <TabsTrigger value="Cancelled" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Cancelled</TabsTrigger>
                        <TabsTrigger value="VoluntaryChurned" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Vol. Churned</TabsTrigger>
                        <TabsTrigger value="InvoluntaryChurned" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Invol. Churned</TabsTrigger>
                        <TabsTrigger value="VoluntaryChurnedRecovered" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Vol. Recovered</TabsTrigger>
                        <TabsTrigger value="InvoluntaryChurnedRecovered" className="flex-1 bg-white data-[state=active]:bg-blue-600 h-8">Invol. Recovered</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client</FormLabel>
                    <FormControl>
                      <Input placeholder="Client name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="loanId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Invoice ID" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="quickBooksInvoiceLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>QuickBooks Payment Link</FormLabel>
                    <FormControl>
                      <Input placeholder="QuickBooks payment link" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="stripeInvoiceLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stripe Payment Link</FormLabel>
                    <FormControl>
                      <Input placeholder="Stripe payment link" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="customInvoiceLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Payment Link</FormLabel>
                    <FormControl>
                      <Input placeholder="Custom payment link" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="invoiceDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        value={field.value instanceof Date && !isNaN(field.value.getTime()) 
                          ? field.value.toISOString().split('T')[0] 
                          : ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val ? new Date(val) : null);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        value={field.value instanceof Date && !isNaN(field.value.getTime()) 
                          ? field.value.toISOString().split('T')[0] 
                          : ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val ? new Date(val) : null);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Column 2 */}
            <div className="space-y-4">
              <FormField
                control={updateAccountReceivableForm.control}
                name="balance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Balance</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Balance"
                        value={field.value}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*\.?\d*$/.test(val)) field.onChange(val);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="amountPaid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Paid</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Amount Paid"
                        value={field.value}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*\.?\d*$/.test(val)) field.onChange(val);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="amountDue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Due</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Auto-calc" value={field.value} readOnly />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="contactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="contactPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Phone</FormLabel>
                    <FormControl>
                      <PhoneInput
                        country="us"
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        inputProps={{ name: 'contactPhone', required: true }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Column 3 */}
            <div className="space-y-4">
              <FormField
                control={updateAccountReceivableForm.control}
                name="streetAddress1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address 1</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Street Address 1"
                        onChange={(e) => handleAddressChange(e.target.value)}
                      />
                    </FormControl>
                    {suggestions.length > 0 && (
                      <ul className="mt-2 max-h-32 overflow-auto rounded-md border bg-white p-2">
                        {suggestions.map((suggestion) => (
                          <li
                            key={suggestion.place_id}
                            className="cursor-pointer p-2 hover:bg-gray-100"
                            onClick={() =>
                              handleSelectSuggestion(suggestion.place_id, suggestion.description)
                            }
                          >
                            {suggestion.description}
                          </li>
                        ))}
                      </ul>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="streetAddress2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address 2</FormLabel>
                    <FormControl>
                      <Input placeholder="Apt, Suite, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input placeholder="State" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Postal Code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={updateAccountReceivableForm.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input placeholder="Country" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
